import { z } from 'zod';
import { getBaseOasisValidationSchema } from '../../oasis';
import { SectionC } from '../../oasis-sections/section-c';
import { isActive } from '../util';
/**
 *
 * Note: if there's an overlapping error for a particular field this is the order in which they'll be displayed
 * form level
 * section level
 * field level
 * base level: same level as "field", but uses built int zod validations
 */
// eslint-disable-next-line
export const sectionCConsistency = ({ input, ctx, version, isSection, field }) => {
    const schema = getBaseOasisValidationSchema('e1', 'soc'); // args here doesn't have a purpose
    if (!isSection && !field) {
        // form level
        const payload = input;
        // C-consistency-5800
        if (payload.SectionBC?.c0100 === '1' &&
            isActive(SectionC.c0200, version, payload.SectionA?.m0100) &&
            isActive(SectionC.c0300, version, payload.SectionA?.m0100) &&
            isActive(SectionC.c0400, version, payload.SectionA?.m0100) &&
            isActive(SectionC.c0500, version, payload.SectionA?.m0100)) {
            const fields = [
                { value: payload.SectionBC?.c0200, path: ['c0200'] },
                { value: payload.SectionBC?.c0300?.c0300a, path: ['c0300', 'c0300a'] },
                { value: payload.SectionBC?.c0300?.c0300b, path: ['c0300', 'c0300b'] },
                { value: payload.SectionBC?.c0300?.c0300c, path: ['c0300', 'c0300c'] },
                { value: payload.SectionBC?.c0400?.c0400a, path: ['c0400', 'c0400a'] },
                { value: payload.SectionBC?.c0400?.c0400b, path: ['c0400', 'c0400b'] },
                { value: payload.SectionBC?.c0400?.c0400c, path: ['c0400', 'c0400c'] },
            ];
            const c0500 = Number(payload.SectionBC?.c0500);
            if (fields.every(({ value }) => value !== '-')) {
                const zeroValueCount = fields.filter(({ value }) => value === '0').length;
                const bimsCount = fields.reduce((acc, { value }) => {
                    const numValue = Number(value);
                    return acc + (isNaN(numValue) ? 0 : numValue);
                }, 0);
                // C-consistency-5800 (a) If three or fewer of the BIMS component items are equal to [0], then C0500 must equal the sum of the values of the component items.
                if (zeroValueCount <= 3 && bimsCount !== c0500) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '-5800 (a) If three or fewer of the BIMS component items are equal to 0, then C0500 must equal the sum of the values of the component items.',
                        path: ['SectionBC', 'c0500'],
                    });
                }
                // C-consistency-5800 (b) If four or more of the BIMS component items are equal to [0] then C0500 must equal the sum of the values of the component items OR it must equal [99].
                if (zeroValueCount >= 4 && bimsCount !== c0500 && c0500 !== 99) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: '-5800 (b) If four or more of the BIMS component items are equal to 0 then C0500 must equal the sum of the values of the component items OR it must equal 00.',
                        path: ['SectionBC', 'c0500'],
                    });
                }
            }
            // C-consistency-5800 (c)  If some, but not all, of the BIMS component items have a value of [-], then C0500 must equal [99].
            if (fields.some(({ value }) => value === '-') && !fields.every(({ value }) => value === '-') && c0500 !== 99) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5800 (c) If some, but not all, of the BIMS component items have a value of "No Information Available", then C0500 must equal 99.',
                    path: ['SectionBC', 'c0500'],
                });
            }
            // C-consistency-5800 (d) If all of the BIMS component items have a value of [-], then C0500 must equal [-].
            if (fields.every(({ value }) => value === '-') && payload.SectionBC?.c0500 !== '-') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5800 (d) If all of the BIMS component items have a value of "No Information Available", then C0500 must equal "No Information Available".',
                    path: ['SectionBC', 'c0500'],
                });
            }
            fields.forEach((field) => {
                if (!field.value) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['SectionBC', ...field.path],
                    });
                }
            });
        }
    }
};
// C-consistency-5800 The following rules refer to the \"BIMS component items\" which are C0200, C0300A, C0300B, C0300C, C0400A, C0400B and C0400C:
//   (a) If all of the BIMS component items are active and have numeric values (not dash) and if three or fewer of the BIMS component items are equal to [0], then C0500 must equal the sum of the values of the component items.
//   (b) If all of the BIMS component items are active and have numeric values (not dash) and if four or more of the BIMS component items are equal to [0] then C0500 must equal the sum of the values of the component items OR it must equal [99].
//   (c)  If some, but not all, of the BIMS component items have a value of [-], then C0500 must equal [99].
//   (d) If all of the BIMS component items have a value of [-], then C0500 must equal [-].
